import React, { useEffect } from "react";
import { Formik, FormikProps } from "formik";
import CircleCheckTransparentIcon from "../../../../icons/circle-check-transparent.icon";
import {
  IAddMemberFormValues,
  addMemberValidationSchema,
  getMemberFormInitialValues,
} from "../../../../../common/validations/members-access.validations";
import { availableRolesList } from "../../memebrs-table/roles.mock-data";
import RoleBadges from "../../role-badges";
import styles from "./index.module.scss";

interface ModalFormProps {
  onFormStateChange?: (values: IAddMemberFormValues, isValid: boolean) => void;
  onSubmit?: (values: IAddMemberFormValues) => void | Promise<void>;
}

interface FormStateTrackerProps {
  values: IAddMemberFormValues;
  isValid: boolean;
  onFormStateChange?: (values: IAddMemberFormValues, isValid: boolean) => void;
  children: React.ReactNode;
}

const FormStateTracker = ({
  values,
  isValid,
  onFormStateChange,
  children,
}: FormStateTrackerProps) => {
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange(values, isValid);
    }
  }, [values, isValid, onFormStateChange]);

  return <>{children}</>;
};

const ModalForm = ({
  onFormStateChange,
  onSubmit,
}: ModalFormProps) => {
  const handleSubmit = async (values: IAddMemberFormValues) => {
    if (onSubmit) {
      await onSubmit(values);
    }
  };

  return (
    <Formik
      initialValues={getMemberFormInitialValues()}
      validationSchema={addMemberValidationSchema}
      onSubmit={handleSubmit}
      validateOnMount
    >
      {(formikProps: FormikProps<IAddMemberFormValues>) => {
        const handleAddRole = (roleValue: string) => {
          const updatedRoles = [...formikProps.values.role, roleValue];
          formikProps.setFieldValue("role", updatedRoles);
        };

        const handleRemoveRole = (roleToRemove: string) => {
          const updatedRoles = formikProps.values.role.filter(
            (role) => role !== roleToRemove
          );
          formikProps.setFieldValue("role", updatedRoles);
        };

        return (
          <FormStateTracker
            values={formikProps.values}
            isValid={formikProps.isValid}
            onFormStateChange={onFormStateChange}
          >
            <div className={styles.container}>
              <div
                className={styles.successAlert}
                role="alert"
                aria-live="polite"
              >
                <div className={styles.iconWrapper}>
                  <CircleCheckTransparentIcon
                    checked={true}
                    width={16}
                    height={16}
                  />
                </div>

                <div className={styles.alertContent}>
                  <p className={styles.alertText}>
                    Users will receive a notification to verify account
                  </p>
                </div>
              </div>

              <form className={styles.form}>
                <div className={styles.formContent}>
                  <div className={styles.fieldGroup}>
                    <label htmlFor="userName" className={styles.label}>
                      User Name
                    </label>

                    <div className={styles.inputWrapper}>
                      <input
                        type="text"
                        id="userName"
                        name="userName"
                        value={formikProps.values.userName}
                        onChange={formikProps.handleChange}
                        onBlur={formikProps.handleBlur}
                        className={styles.input}
                        aria-label="User Name"
                      />
                    </div>
                  </div>

                  <div className={styles.fieldGroup}>
                    <label htmlFor="email" className={styles.label}>
                      Email
                    </label>

                    <div className={styles.inputWrapper}>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formikProps.values.email}
                        onChange={formikProps.handleChange}
                        onBlur={formikProps.handleBlur}
                        className={styles.input}
                        aria-label="Email"
                      />
                    </div>
                  </div>

                  <div className={styles.fieldGroup}>
                    <label htmlFor="role" className={styles.label}>
                      Role
                    </label>

                    <div className={styles.inputWrapper}>
                      <RoleBadges
                        roles={formikProps.values.role}
                        availableRoles={availableRolesList}
                        onAddRole={handleAddRole}
                        onRemoveRole={handleRemoveRole}
                      />
                    </div>
                  </div>

                  <p className={styles.helpText}>
                    User will be required to set a new password as soon as they
                    log in
                  </p>
                </div>
              </form>
            </div>
          </FormStateTracker>
        );
      }}
    </Formik>
  );
};

export default ModalForm;